import type { Editor } from '@tiptap/core';
import type { DragObjectWithType } from 'react-dnd';

import { isSingleEditorTaskList } from '../../../../common/taskLists/taskListContentVersionUtils';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { Point } from '../../../../common/maths/geometry/pointTypes';

export type TaskItemDrag = DragObjectWithType & {
    taskItemDrag?: { sourceElementId: string; sourceTaskId?: string };
    element?: ImMNElement;
    draggedElementIds?: string[];
    hoveredTypesRegistry?: Record<string, string>;
    scaledCustomDragOffset?: Point;
};

/**
 * Checks whether an element drag represents exactly one source V2 task-list element.
 */
export const isTaskListElementDrag = ({
    item,
    targetElementId,
}: {
    item: TaskItemDrag;
    targetElementId: string;
}): boolean =>
    !item.taskItemDrag &&
    item.draggedElementIds?.length === 1 &&
    !!item.element &&
    getElementId(item.element) !== targetElementId &&
    isSingleEditorTaskList(item.element);

/**
 * Determines whether a V2 task list accepts the current drag, without consulting a React-DnD
 * monitor. React-DnD's own lookups throw once a nested task-item target is unregistered
 * mid-drag, so anything rendered during a drag needs a registration-independent answer.
 */
export const doesTaskListAcceptDrag = ({
    item,
    editor,
    currentBoardId,
    targetElementId,
}: {
    item: TaskItemDrag | null;
    editor: Editor | null;
    currentBoardId: string | null;
    targetElementId: string;
}): boolean => {
    if (!item) return false;
    if (!editor) return false;

    if (item.taskItemDrag) return true;
    if (!currentBoardId) return false;

    return isTaskListElementDrag({ item, targetElementId });
};

/**
 * Finds the dragged task's own rendered row, when the drag started in the list being hovered.
 *
 * A row is the task item's node view wrapper, so it contains the rows of its sub-tasks as well.
 */
export const getSourceTaskRow = ({
    rows,
    taskItemDrag,
    targetElementId,
}: {
    rows: HTMLElement[];
    taskItemDrag: TaskItemDrag['taskItemDrag'];
    targetElementId: string;
}): HTMLElement | undefined => {
    if (!taskItemDrag) return undefined;

    const { sourceElementId, sourceTaskId } = taskItemDrag;

    if (!sourceTaskId || sourceElementId !== targetElementId) return undefined;

    return rows.find((row) => row.dataset.taskId === sourceTaskId);
};

/**
 * Gets the insertion gap that follows the dragged task's entire subtree.
 */
const getIndexAfterSourceSubtree = ({
    rows,
    sourceTaskRow,
    sourceIndex,
}: {
    rows: HTMLElement[];
    sourceTaskRow: HTMLElement;
    sourceIndex: number;
}): number => {
    // `contains` reports the row itself as well, so this walks the source row and its sub-task
    // rows, stopping at the first row that sits outside the subtree.
    let index = sourceIndex;

    while (index < rows.length && sourceTaskRow.contains(rows[index])) index++;

    return index;
};

/**
 * Determines whether moving the dragged task into the hovered gap would leave the document
 * unchanged, so the gap should render no indicator and produce no drop result.
 *
 * The gaps touching the dragged task's own subtree are its current location, so a move there does
 * nothing. Copies never take this path: they always add a task item, and they snap out of the
 * source's subtree instead (see `getCopyInsertionIndex`).
 */
export const isNoOpTaskItemDrop = ({
    rows,
    insertionIndex,
    taskItemDrag,
    targetElementId,
}: {
    rows: HTMLElement[];
    insertionIndex: number;
    taskItemDrag: TaskItemDrag['taskItemDrag'];
    targetElementId: string;
}): boolean => {
    const sourceTaskRow = getSourceTaskRow({ rows, taskItemDrag, targetElementId });

    if (!sourceTaskRow) return false;

    return [rows[insertionIndex - 1], rows[insertionIndex]].some((row) => !!row && sourceTaskRow.contains(row));
};

type CopyDropArgs = {
    rows: HTMLElement[];
    insertionIndex: number;
    taskItemDrag: TaskItemDrag['taskItemDrag'];
    targetElementId: string;
    clientY: number;
};

/**
 * Clamps an Option-drag copy's insertion gap out of the dragged task's own subtree. Hovering
 * anywhere within the subtree snaps to the gap immediately above the source row, or the gap after
 * its whole subtree, whichever the pointer is nearer.
 *
 * This constrains the vertical axis only. Keeping the duplicate out of the original also needs the
 * depth axis constrained, because the gap after the subtree still offers the lists nested inside
 * the source - `getTaskListDropTarget` excludes those via its `excludedTaskListAncestor`.
 *
 * Every other gap is returned unchanged, as is any drag that isn't a task item dragged within the
 * list it came from.
 */
export const getCopyInsertionIndex = ({
    rows,
    insertionIndex,
    taskItemDrag,
    targetElementId,
    clientY,
}: CopyDropArgs): number => {
    const sourceTaskRow = getSourceTaskRow({ rows, taskItemDrag, targetElementId });

    if (!sourceTaskRow) return insertionIndex;

    const sourceIndex = rows.indexOf(sourceTaskRow);
    const indexAfterSubtree = getIndexAfterSourceSubtree({ rows, sourceTaskRow, sourceIndex });

    // Both boundary gaps are outside the subtree, so only the gaps strictly between them snap
    if (insertionIndex <= sourceIndex || insertionIndex >= indexAfterSubtree) return insertionIndex;

    // The row wraps its sub-task rows, so its rect spans the whole subtree
    const subtreeRect = sourceTaskRow.getBoundingClientRect();

    return clientY < subtreeRect.top + subtreeRect.height / 2 ? sourceIndex : indexAfterSubtree;
};

/**
 * Resolves where an Option-drag copy actually lands: the snapped insertion gap, plus the ancestor
 * whose nested task lists must be excluded from the depths that gap offers.
 *
 * Both halves are needed to keep a duplicate out of the original it was taken from, so they are
 * resolved together - the hover indicator and the drop must derive from the same pair.
 */
export const getCopyDropAdjustments = (
    args: CopyDropArgs,
): { insertionIndex: number; excludedTaskListAncestor: HTMLElement | undefined } => ({
    insertionIndex: getCopyInsertionIndex(args),
    excludedTaskListAncestor: getSourceTaskRow(args),
});
